<template>
    <div>
        <div class="modal is-active">
            <div class="modal-background"></div>
            <div class="modal-card image-modal">
                <header class="modal-card-head">
                    <p class="modal-card-title">Select Image</p>
                    <button class="delete" aria-label="close" @click="close" :disabled="uploading"></button>
                </header>
                <section class="modal-card-body">
                    <div class="box">
                        <div class="level">
                            <div class="level-left">
                                <div class="file" :class="{uploading: uploading}">
                                    <label class="file-label">
                                        <input class="file-input" @change="fileChange" :disabled="uploading" type="file"
                                               name="resume"
                                               accept="image/*">
                                        <span class="file-cta">
                                        <span class="file-icon">
                                          <font-awesome-icon :icon="uploading ? 'sync' : 'upload'" :spin="uploading"/>
                                        </span>
                                        <span class="file-label">
                                          {{uploading ? 'Uploading...' : 'Upload'}}
                                        </span>
                                    </span>
                                    </label>
                                </div>
                            </div>
                            <progress class="progress is-success" :value="progress" max="100"
                                      style="margin-left: 1rem"></progress>
                        </div>
                    </div>
                    <div class="field search-bar">
                        <div class="control has-icons-left">
                            <!--suppress HtmlFormInputWithoutLabel -->
                            <input class="input" type="text" placeholder="Search" v-model="searchQuery">
                            <span class="icon is-small is-left">
                              <font-awesome-icon icon="search"/>
                            </span>
                        </div>
                        <span class="icon is-medium" :class="{'is-active': !listLayout}" @click="listLayout = false">
                          <font-awesome-icon icon="th"/>
                        </span>
                        <span class="icon is-medium" :class="{'is-active': listLayout}" @click="listLayout = true">
                          <font-awesome-icon icon="th-list"/>
                        </span>
                    </div>
                    <div v-if="listLayout">
                        <div v-for="photo of filteredPhotos" class="image-row">
                            <ImageModalImage
                                :key="photo['.key']"
                                class="image-row-image"
                                :photo="photo"
                                :selected="value === photo.url"
                                @input="$emit('input', $event)"
                                @more="showMoreImage"
                            />
                            <div class="image-row-details">
                                <p>{{ photo.name }}</p>
                                <p class="has-text-grey-light">{{ photo.width }}x{{ photo.height }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="columns is-multiline" v-if="!listLayout">
                        <ImageModalImage
                            v-for="photo of filteredPhotos"
                            :key="photo['.key']"
                            class="column is-3"
                            :photo="photo"
                            :selected="value === photo.url"
                            @input="$emit('input', $event)"
                            @more="showMoreImage"
                        />
                    </div>
                </section>
                <footer class="modal-card-foot">
                    <button class="button is-primary" @click="select" :disabled="uploading || value === ''">Select
                    </button>
                    <button class="button is-danger is-outlined" :disabled="value === ''" @click="remove">Remove
                        Selection
                    </button>
                    <button class="button" @click="close" :disabled="uploading">Cancel</button>
                </footer>
            </div>
        </div>
        <div class="modal" :class="{'is-active': moreImageModalShown}">
            <div class="modal-background"></div>
            <div class="modal-card more-image-modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">{{moreImageModalFileName}}</p>
                    <button @click="moreImageModalShown = false" class="delete" aria-label="close"></button>
                </header>
                <section class="modal-card-body">
                    <figure class="image is-3by2"><img :src="contentUrlPrefix + moreImageModalUrl"></figure>
                </section>
                <footer class="modal-card-foot">
                    <button class="button" @click="moreImageModalShown = false">Done</button>
                    <button class="button is-danger is-outlined" @click="deleteImage">Delete
                        Image
                    </button>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapState} from 'vuex';
    import {contentUrlPrefix, storage} from "../firebase";
    import ImageModalImage from "./ImageModalImage";
    import Fuse from "fuse.js";

    export default {
        name: 'image-modal',
        components: {ImageModalImage},
        props: {
            value: {
                type: String,
                default: ''
            }
        },
        data() {
            return {
                contentUrlPrefix,
                uploading: false,
                progress: 0,
                moreImageModalShown: false,
                moreImageModalUrl: "",
                moreImageModalFileName: "",
                searchQuery: "",
                listLayout: false
            }
        },
        computed: {
            ...mapState(['photos']),
            reversedPhotos() {
                return this.photos.slice().reverse();
            },
            filteredPhotos() {
                if(this.searchQuery === "") {
                    return this.reversedPhotos;
                } else {
                    const fuse = new Fuse(this.reversedPhotos, { keys: ["name"] });
                    return fuse.search(this.searchQuery);
                }
            }
        },
        methods: {
            close() {
                this.progress = 0;
                this.$emit('close');
            },
            select() {
                this.close();
                this.$emit('select');
            },
            remove() {
                this.$emit('input', '');
                this.select();
            },
            fileChange(e) {
                this.uploading = true;

                const file = e.target.files[0];
                const name = file.name;
                const ext = file.name.substring(name.lastIndexOf("."));
                const path = new Date().getTime().toString() + ext;

                const storageRef = storage.ref(path);
                const task = storageRef.put(file, {
                    cacheControl: "public, max-age=31536000, immutable" // 1 year
                });
                task.on('state_changed',
                    (snapshot) => {
                        this.progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    },
                    (err) => {

                    },
                    () => {
                        const url = contentUrlPrefix + path;
                        const img = new Image();
                        const that = this;
                        img.onload = function () {
                            that.$store.$firebaseRefs.photos.push({
                                url: path,
                                name: name,
                                width: this.width,
                                height: this.height
                            });
                            that.$emit('input', path);
                            that.uploading = false;
                        };
                        img.src = url;
                    }
                );
            },
            showMoreImage(photo) {
                this.moreImageModalShown = true;
                this.moreImageModalUrl = photo.url;
                this.moreImageModalFileName = photo.name;
            },
            deleteImage() {
                const url = this.moreImageModalUrl;
                this.moreImageModalShown = false;
                if (this.value === url) {
                    this.$emit('input', '');
                }
                let photo = this.photos.find(photo => photo.url === url);
                this.$store.$firebaseRefs.photos.child(photo[".key"]).remove();

                const storageRef = storage.ref(photo.url);
                storageRef.delete().then(() => console.log("Deleted!")).catch((err) => console.error(err));
            }
        }
    }
</script>

<style lang="scss">
    @import "~bulma/sass/utilities/initial-variables";
    @import "~bulma/sass/utilities/derived-variables";
    @import "~bulma/sass/utilities/mixins";

    .image-modal {
        height: calc(100vh - 40px);

        .modal-card-body {
            overflow-y: scroll;
        }

        .search-bar {
            margin-bottom: 1.5rem !important;
            display: flex;
            flex-direction: row;
            align-items: center;

            .control {
                flex-grow: 1;
                margin-right: 8px;
            }

            .icon {
                opacity: 0.3;
                cursor: pointer;
                transition: opacity 0.1s ease-in-out;
                &:hover {
                    opacity: 0.6;
                }
                &.is-active {
                    opacity: 1;
                }
            }
        }

        .image-row {
            display: flex;
            flex-direction: row;
            margin-bottom: 1.5rem;
            align-items: center;

            .image-row-image {
                min-width: calc(25% - 18px);
            }

            .image-row-details {
                margin-left: 1rem;
                flex-grow: 1;
            }
        }
    }

    .file.uploading .file-label {
        cursor: not-allowed;
    }

    @include tablet {
        .modal-card.more-image-modal-card {
            min-width: 640px;
            width: 50vw;
        }
    }
</style>